import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import Spinner from '../../../components/Spinner';
import { AuthContext } from '../../../contexts/AuthContext';
import GlobalConfigService, { GlobalConfigFormPayload } from '../../../services/global-config.service';
import MasterLookupService from '../../../services/master-lookup.service';

const HEX_COLOUR_REGEX = /^#[0-9A-Fa-f]{6}$/;

interface CurrencyOption {
  id: number;
  currency_code: string;
  currency_name: string;
}

interface TimezoneOption {
  id: number;
  tz_code: string;
  tz_name: string;
}

interface GlobalConfigFormState {
  retention_window_days: number | string;
  renewal_reminder_days: number | string;
  grace_period_days: number | string;
  default_currency_id: number | '';
  default_timezone_id: number | '';
  default_theme_colour: string;
  notes: string;
}

const initialForm: GlobalConfigFormState = {
  retention_window_days: '',
  renewal_reminder_days: 30,
  grace_period_days: '',
  default_currency_id: '',
  default_timezone_id: '',
  default_theme_colour: '',
  notes: '',
};

const GlobalConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const { isSystemAdmin } = useContext(AuthContext);
  const canEdit = isSystemAdmin === 'true' || isSystemAdmin === true;

  const [formData, setFormData] = useState<GlobalConfigFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [timezones, setTimezones] = useState<TimezoneOption[]>([]);

  useEffect(() => {
    MasterLookupService.list({ type: 'currencies', includeDeleted: false, limit: 100 }).then((response: any) => {
      setCurrencies(response?.data?.items ?? []);
    });
    MasterLookupService.list({ type: 'timezones', includeDeleted: false, limit: 100 }).then((response: any) => {
      setTimezones(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    setFetching(true);
    GlobalConfigService.get()
      .then((response: any) => {
        const row = response?.data;
        if (row) {
          setConfigured(true);
          setFormData({
            retention_window_days: row.retention_window_days ?? '',
            renewal_reminder_days: row.renewal_reminder_days ?? 30,
            grace_period_days: row.grace_period_days ?? '',
            default_currency_id: row.default_currency_id ?? '',
            default_timezone_id: row.default_timezone_id ?? '',
            default_theme_colour: row.default_theme_colour ?? '',
            notes: row.notes ?? '',
          });
        } else {
          setConfigured(false);
          setFormData(initialForm);
        }
      })
      .finally(() => setFetching(false));
  }, []);

  const validate = useCallback(
    (data: GlobalConfigFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.retention_window_days || Number(data.retention_window_days) <= 0) {
        nextErrors.retention_window_days = t('system.globalConfig.pageTexts.fields.retentionWindowDays.errors.invalid');
      }
      if (!data.renewal_reminder_days || Number(data.renewal_reminder_days) <= 0) {
        nextErrors.renewal_reminder_days = t('system.globalConfig.pageTexts.fields.renewalReminderDays.errors.invalid');
      }
      if (!data.grace_period_days || Number(data.grace_period_days) <= 0) {
        nextErrors.grace_period_days = t('system.globalConfig.pageTexts.fields.gracePeriodDays.errors.invalid');
      }
      if (!data.default_currency_id) {
        nextErrors.default_currency_id = t('system.globalConfig.pageTexts.fields.defaultCurrency.errors.required');
      }
      if (!data.default_timezone_id) {
        nextErrors.default_timezone_id = t('system.globalConfig.pageTexts.fields.defaultTimezone.errors.required');
      }
      if (data.default_theme_colour && !HEX_COLOUR_REGEX.test(data.default_theme_colour)) {
        nextErrors.default_theme_colour = t('system.globalConfig.pageTexts.fields.defaultThemeColour.errors.invalid');
      }
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const numericFields = ['default_currency_id', 'default_timezone_id'];
    const value = numericFields.includes(name) ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: GlobalConfigFormPayload = {
          retention_window_days: Number(formData.retention_window_days),
          renewal_reminder_days: Number(formData.renewal_reminder_days),
          grace_period_days: Number(formData.grace_period_days),
          default_currency_id: Number(formData.default_currency_id),
          default_timezone_id: Number(formData.default_timezone_id),
          default_theme_colour: formData.default_theme_colour || undefined,
          notes: formData.notes || undefined,
        };
        await GlobalConfigService.update(payload);
        toast.success(<Toast message={t('system.globalConfig.pageTexts.saveSuccess')} />, { icon: false });
        setConfigured(true);
        setEditing(false);
      } catch (err) {
        // interceptor already surfaces the error toast
      }
      setLoading(false);
    },
    [formData, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  const readOnly = !editing;

  return (
    <div className="card mb-6">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{t('system.globalConfig.pageTexts.title')}</h5>
        {canEdit && !editing && (
          <Button className="btn btn-primary" text={t('system.globalConfig.pageTexts.editButton')} onClick={() => setEditing(true)} />
        )}
      </div>
      <hr className="m-0" />
      {!configured && !editing && (
        <div className="card-body">
          <p className="mb-0">{t('system.globalConfig.pageTexts.notConfigured')}</p>
        </div>
      )}
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="retention_window_days"
            name="retention_window_days"
            type="number"
            min="1"
            label={t('system.globalConfig.pageTexts.fields.retentionWindowDays.label')}
            disabled={readOnly}
            required
            value={formData.retention_window_days}
            error={!!errors.retention_window_days}
            errorMessage={errors.retention_window_days}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="renewal_reminder_days"
            name="renewal_reminder_days"
            type="number"
            min="1"
            label={t('system.globalConfig.pageTexts.fields.renewalReminderDays.label')}
            disabled={readOnly}
            required
            value={formData.renewal_reminder_days}
            error={!!errors.renewal_reminder_days}
            errorMessage={errors.renewal_reminder_days}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="grace_period_days"
            name="grace_period_days"
            type="number"
            min="1"
            label={t('system.globalConfig.pageTexts.fields.gracePeriodDays.label')}
            disabled={readOnly}
            required
            value={formData.grace_period_days}
            error={!!errors.grace_period_days}
            errorMessage={errors.grace_period_days}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="default_currency_id">
            {t('system.globalConfig.pageTexts.fields.defaultCurrency.label')}
          </label>
          <select
            id="default_currency_id"
            name="default_currency_id"
            className={`form-select ${errors.default_currency_id ? 'is-invalid' : ''}`}
            disabled={readOnly}
            value={formData.default_currency_id}
            onChange={(e) => handleChange(e, 'default_currency_id')}
          >
            <option value="">{t('system.globalConfig.pageTexts.fields.defaultCurrency.placeholder')}</option>
            {currencies.map((currency) => (
              <option key={currency.id} value={currency.id}>
                {currency.currency_code} — {currency.currency_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="default_timezone_id">
            {t('system.globalConfig.pageTexts.fields.defaultTimezone.label')}
          </label>
          <select
            id="default_timezone_id"
            name="default_timezone_id"
            className={`form-select ${errors.default_timezone_id ? 'is-invalid' : ''}`}
            disabled={readOnly}
            value={formData.default_timezone_id}
            onChange={(e) => handleChange(e, 'default_timezone_id')}
          >
            <option value="">{t('system.globalConfig.pageTexts.fields.defaultTimezone.placeholder')}</option>
            {timezones.map((timezone) => (
              <option key={timezone.id} value={timezone.id}>
                {timezone.tz_name} ({timezone.tz_code})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block">{t('system.globalConfig.pageTexts.fields.defaultThemeColour.label')}</label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="color"
              disabled={readOnly}
              value={HEX_COLOUR_REGEX.test(formData.default_theme_colour) ? formData.default_theme_colour : '#0f172a'}
              onChange={(e) => setFormData((prev) => ({ ...prev, default_theme_colour: e.target.value }))}
            />
            <Input
              id="default_theme_colour"
              name="default_theme_colour"
              placeholder="#0f172a"
              disabled={readOnly}
              value={formData.default_theme_colour}
              error={!!errors.default_theme_colour}
              errorMessage={errors.default_theme_colour}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label d-block">{t('system.globalConfig.pageTexts.fields.notificationChannel.label')}</label>
          <input className="form-control" value="Email" disabled readOnly />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('system.globalConfig.pageTexts.fields.notes.label')}
            disabled={readOnly}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        {editing && (
          <div className="col-12 d-flex gap-2 justify-content-end">
            <Button
              type="button"
              className="btn btn-outline-secondary"
              text={t('system.globalConfig.pageTexts.buttons.cancel')}
              onClick={() => setEditing(false)}
            />
            <Button type="submit" className="btn btn-primary" text={t('system.globalConfig.pageTexts.buttons.save')} loading={loading} />
          </div>
        )}
      </form>
    </div>
  );
};

export default GlobalConfigPage;
